import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import { FaExchangeAlt, FaHome, FaBuilding, FaMapMarkerAlt } from 'react-icons/fa';
import './Sidebar.css';
import './Navbar.css';
import './Compare.css';
import LoadingScreen from './LoadingScreen';
import Tutorial from './Tutorial';

const BASE_URL = 'https://api.rside.app';

function Compare() {
  const navigate = useNavigate();
  const [initials, setInitials] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedPropA, setSelectedPropA] = useState('');
  const [selectedDoorA, setSelectedDoorA] = useState('');
  const [selectedPropB, setSelectedPropB] = useState('');
  const [selectedDoorB, setSelectedDoorB] = useState('');

  const [dataA, setDataA] = useState(null);
  const [dataB, setDataB] = useState(null);
  const [loadingA, setLoadingA] = useState(false);
  const [loadingB, setLoadingB] = useState(false);
  const [activityPageA, setActivityPageA] = useState(1);
  const [activityPageB, setActivityPageB] = useState(1);
  const activityPerPage = 5;

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return navigate('/login');
      setUserEmail(user.email);
      try {
        const res = await fetch(`${BASE_URL}/PropertyManagerName?email=${user.email}`);
        const data = await res.json();
        const parts = data.Full_Name?.split(' ') || [];
        setInitials((parts[0]?.[0] || '') + (parts.at(-1)?.[0] || ''));

        const propRes = await fetch(
          `${BASE_URL}/GetAllProperties?full_name=${encodeURIComponent(data.Full_Name)}&Email=${encodeURIComponent(user.email)}`
        );
        if (propRes.ok) {
          const propData = await propRes.json();
          const propertiesMap = new Map();
          for (const p of propData.Properties || []) {
            const propId = p.Property_ID;
            if (!propertiesMap.has(propId)) {
              propertiesMap.set(propId, {
                id: propId,
                name: p.Property_Name,
                propertyType: parseInt(p.Num_Of_Units) > 1 ? 'Multi Family Home' : 'Single Family Home',
                numberOfUnits: p.Num_Of_Units,
                status: p.Status || 'Vacant',
                address: p.Street_Address,
                doors: []
              });
            }
            propertiesMap.get(propId).doors.push({
              doorId: p.Door_id,
              unitNumber: p.Unit_Number || '',
              status: p.Status || 'Vacant'
            });
          }
          setProperties(Array.from(propertiesMap.values()).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        }
      } catch (err) {
        console.error('Error loading properties:', err);
      } finally {
        setLoading(false);
      }
    });
    return unsub;
  }, [navigate]);

  // Reset door selection when property changes
  useEffect(() => { setSelectedDoorA(''); }, [selectedPropA]);
  useEffect(() => { setSelectedDoorB(''); }, [selectedPropB]);

  const fetchPropertyData = async (propertyId, doorId = null) => {
    const user = auth.currentUser;
    if (!user) return null;
    const prop = properties.find(p => String(p.id) === String(propertyId));
    if (!prop) return null;

    const selectedDoor = doorId ? prop.doors.find(d => String(d.doorId) === String(doorId)) : null;

    const result = {
      ...prop,
      displayName: doorId && selectedDoor
        ? `${prop.name} — Unit ${selectedDoor.unitNumber || doorId}`
        : prop.name,
      doors: [],
      purchasePrice: '', totalCost: '', mortgagePayment: '',
      propertyTaxes: '', propertyInsurance: '', hoa: '',
      totalIncome: 0, totalExpenses: 0, netCashFlow: 0,
      noi: null, capRate: null, cocReturn: null,
      activities: []
    };

    try {
      const detailsRes = await fetch(
        `${BASE_URL}/GetPropertyDetails?property_name=${encodeURIComponent(prop.name)}&email=${encodeURIComponent(user.email)}`
      );
      if (detailsRes.ok) {
        const detailsData = await detailsRes.json();
        const allDoors = detailsData.results || [];
        const doorsToProcess = doorId
          ? allDoors.filter(d => String(d.door_id) === String(doorId))
          : allDoors;

        for (const door of doorsToProcess) {
          if (!door.door_id) continue;
          const doorInfo = {
            doorId: door.door_id, unitNumber: door.Unit_Number || '',
            status: 'Vacant', tenantName: '', phoneNumber: '',
            rent: '', deposit: '', leaseStartDate: '', leaseEndDate: '', leaseType: ''
          };
          try {
            const tenantRes = await fetch(`${BASE_URL}/Lease&TenantsByDoor?door_id=${door.door_id}`);
            if (tenantRes.ok) {
              const tenantData = await tenantRes.json();
              const tenant = tenantData.tenants?.[0] || tenantData.tenant || (Array.isArray(tenantData) ? tenantData[0] : null);
              if (tenant) {
                doorInfo.tenantName = tenant.Full_Name || '';
                doorInfo.phoneNumber = tenant.Phone_Number || '';
                doorInfo.rent = tenant.Rent || '';
                doorInfo.deposit = tenant.Deposit || '';
                doorInfo.leaseStartDate = tenant.Start_Date || '';
                doorInfo.leaseEndDate = tenant.End_Date || '';
                doorInfo.leaseType = tenant.MonthToMonth ? 'Month-to-Month' : 'Fixed';
                doorInfo.status = door.Stat || (tenant.Full_Name ? 'Occupied' : 'Vacant');
              } else {
                doorInfo.status = door.Stat || 'Vacant';
              }
            }
          } catch {}
          result.doors.push(doorInfo);
        }

        // Activities per door
        const allActivities = [];
        for (const door of doorsToProcess) {
          if (!door.door_id) continue;
          try {
            const rentRes = await fetch(`${BASE_URL}/RentByDoor?door_id=${door.door_id}`);
            if (rentRes.ok) {
              const rentData = await rentRes.json();
              let rents = Array.isArray(rentData.items) ? rentData.items
                : Array.isArray(rentData) ? rentData
                : Array.isArray(rentData.rent_payments) ? rentData.rent_payments
                : Array.isArray(rentData.RentPayments) ? rentData.RentPayments
                : Object.values(rentData || {}).filter(v => typeof v === 'object');
              while (Array.isArray(rents) && Array.isArray(rents[0])) rents = rents.flat();
              rents.forEach((r, idx) => {
                const amount = r.amount ?? r.Amount ?? r.rent ?? r.Rent ?? 0;
                const date = r.Paydate || r.paydate || r.PayDate || '';
                allActivities.push({
                  id: `rent-${door.door_id}-${idx}`,
                  amount: Math.abs(parseFloat(amount) || 0),
                  type: 'Rent', rawDate: date,
                  date: date ? new Date(date).toLocaleDateString() : '',
                  unitNumber: door.Unit_Number || ''
                });
              });
            }
          } catch {}

          try {
            const expRes = await fetch(`${BASE_URL}/ExpensesByDoor?door_id=${door.door_id}`);
            if (expRes.ok) {
              const expData = await expRes.json();
              let expenses = Array.isArray(expData) ? expData
                : Array.isArray(expData.expenses) ? expData.expenses
                : Array.isArray(expData.Expenses) ? expData.Expenses
                : Object.values(expData || {}).filter(v => typeof v === 'object');
              expenses.forEach((e, idx) => {
                const amount = e.amount ?? e.Amount ?? 0;
                const date = e.paydate || e.PayDate || e.Paydate || '';
                allActivities.push({
                  id: `exp-${door.door_id}-${idx}`,
                  amount: -Math.abs(parseFloat(amount) || 0),
                  type: e.category || 'Expense', rawDate: date,
                  date: date ? new Date(date).toLocaleDateString() : '',
                  unitNumber: door.Unit_Number || ''
                });
              });
            }
          } catch {}
        }

        allActivities.sort((a, b) => {
          const dA = a.rawDate ? new Date(a.rawDate).getTime() : 0;
          const dB = b.rawDate ? new Date(b.rawDate).getTime() : 0;
          return dB - dA;
        });
        result.activities = allActivities;
      }
    } catch (err) {
      console.error('Error fetching property details:', err);
    }

    try {
      const finRes = await fetch(`${BASE_URL}/PropertyFinancials?property_id=${propertyId}`);
      if (finRes.ok) {
        const finData = await finRes.json();
        const fin = finData.financials?.[0];
        if (fin) {
          result.purchasePrice = fin.Purchase_Price || '';
          result.totalCost = fin.Total_Cost || '';
          result.mortgagePayment = fin.Mortgage_Payment || '';
          result.propertyTaxes = fin.Tax || '';
          result.propertyInsurance = fin.Insurance || fin.Insurance_Annual || fin.insurance_annual || '';
          result.hoa = fin.HOA || '';
        }
      }
    } catch {}

    // Pull financial metrics from the same APIs the report page uses
    try {
      const now = new Date();
      const ey = now.getFullYear();
      const em = now.getMonth() + 1;
      const tqs = `property_id=${propertyId}&start_month=1&end_month=${em}&start_year=${ey}&end_year=${ey}`;

      const [cashRes, noiRes, capRes, cocRes] = await Promise.all([
        fetch(`${BASE_URL}/PropertyCashFlowYTD?${tqs}`),
        fetch(`${BASE_URL}/NOIByProperty?${tqs}&mode=YTD`),
        fetch(`${BASE_URL}/CapRateByProperty?property_id=${propertyId}&pm_email=${encodeURIComponent(user.email)}`),
        fetch(`${BASE_URL}/CashOnCashByProperty?property_id=${propertyId}`),
      ]);

      if (cashRes.ok) {
        const d = await cashRes.json();
        result.totalIncome   = Number(d.total_rent_ytd)     || 0;
        result.totalExpenses = Number(d.total_expenses_ytd) || 0;
        result.netCashFlow   = Number(d.cash_flow_ytd)      || 0;
      }

      if (noiRes.ok) {
        const d = await noiRes.json();
        const r = d?.result || d || {};
        const v = r.noi_ytd ?? r.NOI_YTD ?? r.noi ?? null;
        result.noi = v !== null ? Number(v) : null;
      }

      if (capRes.ok) {
        const d = await capRes.json();
        const r = d?.result || d || {};
        const noi = r.noi_ytd ?? null;
        const pv  = r.property_value ?? null;
        result.capRate = (noi !== null && pv !== null && Number(pv) > 0)
          ? Number(((Number(noi) / Number(pv)) * 100).toFixed(2))
          : null;
      }

      if (cocRes.ok) {
        const d = await cocRes.json();
        const r = d?.result || d || {};
        const v = r.coc_ytd ?? null;
        result.cocReturn = v !== null ? Number(v) : null;
      }
    } catch {}

    return result;
  };

  useEffect(() => {
    if (!selectedPropA) { setDataA(null); return; }
    setActivityPageA(1);
    setLoadingA(true);
    fetchPropertyData(selectedPropA, selectedDoorA || null).then(data => {
      setDataA(data);
      setLoadingA(false);
    });
  }, [selectedPropA, selectedDoorA, properties]);

  useEffect(() => {
    if (!selectedPropB) { setDataB(null); return; }
    setActivityPageB(1);
    setLoadingB(true);
    fetchPropertyData(selectedPropB, selectedDoorB || null).then(data => {
      setDataB(data);
      setLoadingB(false);
    });
  }, [selectedPropB, selectedDoorB, properties]);

  const fmt = (val) => {
    const num = Number(val) || 0;
    return num < 0 ? `-$${Math.abs(num).toLocaleString('en-US')}` : `$${num.toLocaleString('en-US')}`;
  };

  const propADoors = properties.find(p => String(p.id) === String(selectedPropA))?.doors || [];
  const propBDoors = properties.find(p => String(p.id) === String(selectedPropB))?.doors || [];

  const isSameSelection =
    selectedPropA && selectedPropB &&
    String(selectedPropA) === String(selectedPropB);

  const bothReady = dataA && dataB && !loadingA && !loadingB && !isSameSelection;

  // Row-style comparison component: [A value] [label] [B value]
  const CompRow = ({ label, valA, valB, winSide, valAColor, valBColor }) => (
    <div className="cmpr-row">
      <div className={`cmpr-cell cmpr-cell-a ${winSide === 'a' ? 'cmpr-win-a' : ''}`} style={winSide !== 'a' && valAColor ? { color: valAColor } : {}}>
        {valA || '—'}
        {winSide === 'a' && <span className="cmpr-star">★</span>}
      </div>
      <div className="cmpr-row-label">{label}</div>
      <div className={`cmpr-cell cmpr-cell-b ${winSide === 'b' ? 'cmpr-win-b' : ''}`} style={winSide !== 'b' && valBColor ? { color: valBColor } : {}}>
        {winSide === 'b' && <span className="cmpr-star">★</span>}
        {valB || '—'}
      </div>
    </div>
  );

  const SectionDivider = ({ title }) => (
    <div className="cmpr-section-divider">
      <span>{title}</span>
    </div>
  );

  if (loading) return <LoadingScreen />;

  return (
    <div className="home-page">
      <Sidebar initials={initials} />
      <div className="main-content">
        <Navbar title="Compare" />

        <div className="cmpr-container">

          {/* ── SELECTOR ── */}
          <div id="cmpr-selector" className="cmpr-selector-card">
            <div className="cmpr-sel-side">
              <div className="cmpr-sel-badge cmpr-badge-a">A</div>
              <div className="cmpr-sel-fields">
                <label className="cmpr-sel-label">Property A</label>
                <select className="cmpr-select cmpr-select-a" value={selectedPropA} onChange={e => setSelectedPropA(e.target.value)}>
                  <option value="">Select a property…</option>
                  {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>

            <button className="cmpr-swap-btn" title="Swap" onClick={() => {
              const pA = selectedPropA, dA = selectedDoorA;
              setSelectedPropA(selectedPropB); setSelectedDoorA(selectedDoorB);
              setSelectedPropB(pA); setSelectedDoorB(dA);
            }}>
              <FaExchangeAlt />
            </button>

            <div className="cmpr-sel-side">
              <div className="cmpr-sel-fields">
                <label className="cmpr-sel-label">Property B</label>
                <select className="cmpr-select cmpr-select-b" value={selectedPropB} onChange={e => setSelectedPropB(e.target.value)}>
                  <option value="">Select a property…</option>
                  {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="cmpr-sel-badge cmpr-badge-b">B</div>
            </div>
          </div>

          {isSameSelection && (
            <div className="cmpr-warning">Please select different units or properties to compare.</div>
          )}

          {(loadingA || loadingB) && (
            <div className="cmpr-loading-bar">
              <div className="cmpr-spinner" />
              Loading property data…
            </div>
          )}

          {bothReady && (() => {
            const incomeWin = dataA.totalIncome   > dataB.totalIncome   ? 'a' : dataB.totalIncome   > dataA.totalIncome   ? 'b' : '';
            const expWin    = dataA.totalExpenses < dataB.totalExpenses ? 'a' : dataB.totalExpenses < dataA.totalExpenses ? 'b' : '';
            const cfWin     = dataA.netCashFlow   > dataB.netCashFlow   ? 'a' : dataB.netCashFlow   > dataA.netCashFlow   ? 'b' : '';
            const ppWin     = dataA.purchasePrice && dataB.purchasePrice ? (Number(dataA.purchasePrice) < Number(dataB.purchasePrice) ? 'a' : Number(dataB.purchasePrice) < Number(dataA.purchasePrice) ? 'b' : '') : '';
            const mortWin   = dataA.mortgagePayment && dataB.mortgagePayment ? (Number(dataA.mortgagePayment) < Number(dataB.mortgagePayment) ? 'a' : Number(dataB.mortgagePayment) < Number(dataA.mortgagePayment) ? 'b' : '') : '';

            // Values pulled directly from report APIs — same source as the Report page
            const noiA = dataA.noi !== null ? Number(dataA.noi) : null;
            const noiB = dataB.noi !== null ? Number(dataB.noi) : null;
            const noiWin = noiA !== null && noiB !== null ? (noiA > noiB ? 'a' : noiB > noiA ? 'b' : '') : '';

            const cocA = dataA.cocReturn !== null ? Number(dataA.cocReturn) : null;
            const cocB = dataB.cocReturn !== null ? Number(dataB.cocReturn) : null;
            const cocWin = cocA !== null && cocB !== null ? (cocA > cocB ? 'a' : cocB > cocA ? 'b' : '') : '';

            const capA = dataA.capRate !== null ? Number(dataA.capRate) : null;
            const capB = dataB.capRate !== null ? Number(dataB.capRate) : null;
            const capWin = capA !== null && capB !== null ? (capA > capB ? 'a' : capB > capA ? 'b' : '') : '';

            // Expense Ratio derived from API income/expenses (no dedicated endpoint)
            const erA = dataA.totalIncome > 0 ? (dataA.totalExpenses / dataA.totalIncome) * 100 : null;
            const erB = dataB.totalIncome > 0 ? (dataB.totalExpenses / dataB.totalIncome) * 100 : null;
            const erWin = erA !== null && erB !== null ? (erA < erB ? 'a' : erB < erA ? 'b' : '') : '';

            const winsA = [incomeWin, expWin, cfWin, noiWin, cocWin, capWin, erWin].filter(w => w === 'a').length;
            const winsB = [incomeWin, expWin, cfWin, noiWin, cocWin, capWin, erWin].filter(w => w === 'b').length;
            const leader = winsA > winsB ? 'a' : winsB > winsA ? 'b' : 'tie';


            return (
              <>
                {/* ── VS PROPERTY HEADER ── */}
                <div className="cmpr-vs-bar">
                  <div className="cmpr-prop-card cmpr-prop-a">
                    <div className={`cmpr-pc-icon ${dataA.propertyType === 'Single Family Home' ? 'cpi-green' : 'cpi-orange'}`}>
                      {dataA.propertyType === 'Single Family Home' ? <FaHome /> : <FaBuilding />}
                    </div>
                    <div className="cmpr-pc-info">
                      <div className="cmpr-pc-name">{dataA.displayName}</div>
                      <div className="cmpr-pc-addr"><FaMapMarkerAlt /> {dataA.address || 'N/A'}</div>
                      <div className="cmpr-pc-type">{dataA.propertyType}</div>
                      <div className={`cmpr-wins-pill cmpr-wins-a ${leader === 'a' ? 'cmpr-wins-leader' : ''}`} style={{ marginTop: 6, alignSelf: 'flex-start' }}>
                        {winsA} wins
                      </div>
                    </div>
                  </div>

                  <div className="cmpr-vs-center">
                    <div className="cmpr-vs-badge">VS</div>
                    {leader !== 'tie' && (
                      <div className={`cmpr-leader-label ${leader === 'a' ? 'cmpr-leader-a' : 'cmpr-leader-b'}`}>
                        {leader === 'a' ? 'A leads' : 'B leads'}
                      </div>
                    )}
                    {leader === 'tie' && <div className="cmpr-leader-label" style={{ color: '#9ca3af' }}>Tied</div>}
                  </div>

                  <div className="cmpr-prop-card cmpr-prop-b">
                    <div className="cmpr-pc-info cmpr-pc-info-r">
                      <div className="cmpr-pc-name">{dataB.displayName}</div>
                      <div className="cmpr-pc-addr"><FaMapMarkerAlt /> {dataB.address || 'N/A'}</div>
                      <div className="cmpr-pc-type">{dataB.propertyType}</div>
                      <div className={`cmpr-wins-pill cmpr-wins-b ${leader === 'b' ? 'cmpr-wins-leader' : ''}`} style={{ marginTop: 6, alignSelf: 'flex-end' }}>
                        {winsB} wins
                      </div>
                    </div>
                    <div className={`cmpr-pc-icon ${dataB.propertyType === 'Single Family Home' ? 'cpi-green' : 'cpi-orange'}`}>
                      {dataB.propertyType === 'Single Family Home' ? <FaHome /> : <FaBuilding />}
                    </div>
                  </div>
                </div>

                {/* ── COMPARISON TABLE ── */}
                <div className="cmpr-table-card">

                  {/* Column labels */}
                  <div className="cmpr-col-headers">
                    <div className="cmpr-col-a"><span className="cmpr-badge-a"></span> {dataA.displayName}</div>
                    <div className="cmpr-col-mid" />
                    <div className="cmpr-col-b">{dataB.displayName} <span className="cmpr-badge-b"></span></div>
                  </div>

                  <SectionDivider title="Year-to-Date Summary" />
                  <CompRow label="Total Income"          valA={fmt(dataA.totalIncome)}   valB={fmt(dataB.totalIncome)}   winSide={incomeWin} />
                  <CompRow label="Total Expenses"        valA={fmt(dataA.totalExpenses)} valB={fmt(dataB.totalExpenses)} winSide={expWin} />
                  <CompRow label="Net Cash Flow"         valA={fmt(dataA.netCashFlow)}   valB={fmt(dataB.netCashFlow)}   winSide={cfWin}
                    valAColor={dataA.netCashFlow < 0 ? '#dc2626' : '#16a34a'}
                    valBColor={dataB.netCashFlow < 0 ? '#dc2626' : '#16a34a'} />
                  <CompRow label="Net Operating Income"  valA={noiA !== null ? fmt(noiA) : '—'} valB={noiB !== null ? fmt(noiB) : '—'} winSide={noiWin}
                    valAColor={noiA !== null && noiA < 0 ? '#dc2626' : '#16a34a'}
                    valBColor={noiB !== null && noiB < 0 ? '#dc2626' : '#16a34a'} />
                  <CompRow label="Cash on Cash Return"   valA={cocA !== null ? `${cocA.toFixed(1)}%` : '—'} valB={cocB !== null ? `${cocB.toFixed(1)}%` : '—'} winSide={cocWin} />
                  <CompRow label="Cap Rate"              valA={capA !== null ? `${capA.toFixed(2)}%` : '—'} valB={capB !== null ? `${capB.toFixed(2)}%` : '—'} winSide={capWin} />
                  <CompRow label="Expense Ratio"         valA={erA !== null ? `${erA.toFixed(1)}%` : '—'} valB={erB !== null ? `${erB.toFixed(1)}%` : '—'} winSide={erWin} />

                  <SectionDivider title="Property Info" />
                  <CompRow label="Type"    valA={dataA.propertyType}  valB={dataB.propertyType} />
                  <CompRow label="Units"   valA={dataA.numberOfUnits} valB={dataB.numberOfUnits} />
                  <CompRow label="Address" valA={dataA.address || '—'} valB={dataB.address || '—'} />

                  <SectionDivider title="Financial Details" />
                  <CompRow label="Purchase Price" valA={fmt(dataA.purchasePrice)}    valB={fmt(dataB.purchasePrice)}    winSide={ppWin} />
                  <CompRow label="Total Cost"     valA={fmt(dataA.totalCost)}        valB={fmt(dataB.totalCost)} />
                  <CompRow label="Mortgage / mo"  valA={fmt(dataA.mortgagePayment)}  valB={fmt(dataB.mortgagePayment)}  winSide={mortWin} />
                  <CompRow label="Property Tax"   valA={fmt(dataA.propertyTaxes)}    valB={fmt(dataB.propertyTaxes)} />
                  <CompRow label="Insurance"      valA={fmt(dataA.propertyInsurance)} valB={fmt(dataB.propertyInsurance)} />
                  <CompRow label="HOA"            valA={fmt(dataA.hoa)}              valB={fmt(dataB.hoa)} />

                </div>

                {/* ── LEASE DETAILS ── */}
                <div className="cmpr-split-section">
                  <div className="cmpr-split-header">
                    <span>Lease Details</span>
                  </div>
                  <div className="cmpr-split-cols">
                    <div className="cmpr-lease-col cmpr-lease-a">
                      <div className="cmpr-lease-col-hd"><span className="cmpr-badge-a"></span>{dataA.displayName}</div>
                      {dataA.doors.length === 0 ? <p className="cmpr-empty-text">No units found</p> :
                        dataA.doors.map((door, idx) => (
                          <div key={door.doorId} className="cmpr-door-card">
                            <div className="cmpr-door-hd">
                              <span className="cmpr-door-unit">{dataA.doors.length > 1 ? `Unit ${door.unitNumber || idx + 1}` : 'Unit Info'}</span>
                              <span className={`cmpr-status-badge ${door.status === 'Occupied' ? 'cmpr-st-occ' : 'cmpr-st-vac'}`}>{door.status}</span>
                            </div>
                            {[['Tenant', door.tenantName], ['Phone', door.phoneNumber],
                              ['Rent', door.rent ? fmt(door.rent) : null], ['Deposit', door.deposit ? fmt(door.deposit) : null],
                              ['Lease Start', door.leaseStartDate], ['Lease End', door.leaseEndDate], ['Lease Type', door.leaseType]
                            ].map(([k, v]) => v ? (
                              <div key={k} className="cmpr-door-detail"><span className="cmpr-dd-k">{k}</span><span className="cmpr-dd-v">{v}</span></div>
                            ) : null)}
                          </div>
                        ))
                      }
                    </div>
                    <div className="cmpr-lease-divider" />
                    <div className="cmpr-lease-col cmpr-lease-b">
                      <div className="cmpr-lease-col-hd">{dataB.displayName}<span className="cmpr-badge-b"></span></div>
                      {dataB.doors.length === 0 ? <p className="cmpr-empty-text">No units found</p> :
                        dataB.doors.map((door, idx) => (
                          <div key={door.doorId} className="cmpr-door-card">
                            <div className="cmpr-door-hd">
                              <span className="cmpr-door-unit">{dataB.doors.length > 1 ? `Unit ${door.unitNumber || idx + 1}` : 'Unit Info'}</span>
                              <span className={`cmpr-status-badge ${door.status === 'Occupied' ? 'cmpr-st-occ' : 'cmpr-st-vac'}`}>{door.status}</span>
                            </div>
                            {[['Tenant', door.tenantName], ['Phone', door.phoneNumber],
                              ['Rent', door.rent ? fmt(door.rent) : null], ['Deposit', door.deposit ? fmt(door.deposit) : null],
                              ['Lease Start', door.leaseStartDate], ['Lease End', door.leaseEndDate], ['Lease Type', door.leaseType]
                            ].map(([k, v]) => v ? (
                              <div key={k} className="cmpr-door-detail"><span className="cmpr-dd-k">{k}</span><span className="cmpr-dd-v">{v}</span></div>
                            ) : null)}
                          </div>
                        ))
                      }
                    </div>
                  </div>
                </div>

                {/* ── ACTIVITY ── */}
                <div className="cmpr-split-section">
                  <div className="cmpr-split-header"><span>Activity</span></div>
                  <div className="cmpr-split-cols">
                    <div className="cmpr-act-col">
                      <div className="cmpr-lease-col-hd"><span className="cmpr-badge-a"></span>{dataA.displayName} <span className="cmpr-count">{dataA.activities.length}</span></div>
                      {dataA.activities.length === 0 ? <p className="cmpr-empty-text">No activity</p> : <>
                        <table className="cmpr-act-table">
                          <thead><tr><th>Amount</th><th>Type</th><th>Date</th></tr></thead>
                          <tbody>
                            {dataA.activities.slice((activityPageA - 1) * activityPerPage, activityPageA * activityPerPage).map(a => (
                              <tr key={a.id}>
                                <td className={a.amount >= 0 ? 'cmpr-pos' : 'cmpr-neg'}>{a.amount >= 0 ? '+' : '-'}{fmt(Math.abs(a.amount))}</td>
                                <td>{a.type}{a.unitNumber ? ` (${a.unitNumber})` : ''}</td>
                                <td>{a.date}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {dataA.activities.length > activityPerPage && (
                          <div className="cmpr-pagination">
                            <button className="cmpr-page-btn" disabled={activityPageA === 1} onClick={() => setActivityPageA(p => p - 1)}>‹ Prev</button>
                            <span className="cmpr-page-info">{activityPageA} / {Math.ceil(dataA.activities.length / activityPerPage)}</span>
                            <button className="cmpr-page-btn" disabled={activityPageA >= Math.ceil(dataA.activities.length / activityPerPage)} onClick={() => setActivityPageA(p => p + 1)}>Next ›</button>
                          </div>
                        )}
                      </>}
                    </div>
                    <div className="cmpr-lease-divider" />
                    <div className="cmpr-act-col">
                      <div className="cmpr-lease-col-hd"><span className="cmpr-badge-b"></span>{dataB.displayName} <span className="cmpr-count">{dataB.activities.length}</span></div>
                      {dataB.activities.length === 0 ? <p className="cmpr-empty-text">No activity</p> : <>
                        <table className="cmpr-act-table">
                          <thead><tr><th>Amount</th><th>Type</th><th>Date</th></tr></thead>
                          <tbody>
                            {dataB.activities.slice((activityPageB - 1) * activityPerPage, activityPageB * activityPerPage).map(a => (
                              <tr key={a.id}>
                                <td className={a.amount >= 0 ? 'cmpr-pos' : 'cmpr-neg'}>{a.amount >= 0 ? '+' : '-'}{fmt(Math.abs(a.amount))}</td>
                                <td>{a.type}{a.unitNumber ? ` (${a.unitNumber})` : ''}</td>
                                <td>{a.date}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {dataB.activities.length > activityPerPage && (
                          <div className="cmpr-pagination">
                            <button className="cmpr-page-btn" disabled={activityPageB === 1} onClick={() => setActivityPageB(p => p - 1)}>‹ Prev</button>
                            <span className="cmpr-page-info">{activityPageB} / {Math.ceil(dataB.activities.length / activityPerPage)}</span>
                            <button className="cmpr-page-btn" disabled={activityPageB >= Math.ceil(dataB.activities.length / activityPerPage)} onClick={() => setActivityPageB(p => p + 1)}>Next ›</button>
                          </div>
                        )}
                      </>}
                    </div>
                  </div>
                </div>
              </>
            );
          })()}

          {!selectedPropA && !selectedPropB && (
            <div className="cmpr-empty">
              <div className="cmpr-empty-icon"><FaExchangeAlt /></div>
              <h3>Compare Properties Side by Side</h3>
              <p>Select two properties — or two units within a multi-family property — to instantly compare financials, lease details, and activity.</p>
            </div>
          )}
        </div>
      </div>

      {userEmail && (
        <Tutorial
          steps={[
            { title: 'Welcome to Compare!', description: 'Pick any two properties — or two individual units — and instantly see them side by side. Great for spotting which investment is performing better.', anchorId: null, cardAlignment: 'bottom' },
            { title: 'Select Properties', description: 'Choose Property A from the left dropdown and Property B from the right. For multi-unit properties a second dropdown appears so you can drill down to a specific unit.', anchorId: 'cmpr-selector' },
            { title: 'Swap A & B', description: 'The arrows button in the center instantly flips which property is A and which is B — handy when you want to re-read the comparison from the other direction.', anchorId: 'cmpr-selector' },
            { title: 'See the Results', description: 'Once both are selected you get a full side-by-side breakdown: income, expenses, cash flow, ROI, property details, lease info, and recent transactions. Stars (★) mark the winner in each category.', anchorId: null, cardAlignment: 'bottom' },
          ]}
          storageKey={`hasSeenCompareTutorial_${userEmail}`}
        />
      )}
    </div>
  );
}

export default Compare;
